import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';

import './index.scss';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useDispatch, useSelector} from 'react-redux';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useLocalization} from "../../../../hooks/localization";
import {useRPC} from "../../../../hooks/rpc";
import {changeBusyAction} from '../../../../store/ui/actions';
const keyExtractor = (item: BridgeDeviceModel) => item.id;

export const DeviceListScreen = memo(function DeviceListScreen() {
    const {t} = useLocalization();
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const gatewaySet = useSetSelector(state => state.control.gatewayList);
    const [rpc] = useRPC();
    const [selectedDevice, setSelectedDevice] = useState<BridgeDeviceModel>(null);
    const dispatch = useDispatch();

    const [deviceCountValue, setDeviceCountValue] = useState<number>(0);

    const isBooster = selectedDevice?.type_id === '18' ? true : false


    const onRFTest = useCallback(() => {
        dispatch(changeBusyAction(true))
        rpc.GetDeviceRFSignalStrength()
            .then(() => {
                const timer = setTimeout(() => {
                    dispatch(changeBusyAction(false))
                }, Number(bridgeDeviceList.length * 1000))
                return () => clearTimeout(timer)
            })
            .catch(() => dispatch(changeBusyAction(false)))
    }, [rpc, bridgeDeviceList, dispatch])

    const buttons = useMemo(() => [
        {
            text: t('booster_device_list'),
            className: 'mulberry-wood',
            url: `/settings/device/booster-list/${selectedDevice?.id}`,
            hidden: !isBooster
        },
        {
            text: t('rf_test'),
            className: 'yellow',
            onClick: onRFTest,
        },
        {
            text: t('stp_editor'),
            url: `/settings/stp-editor/${selectedDevice?.id}`,
            className: 'gray',
            hidden: !(selectedDevice?.type_id === '4' || selectedDevice?.type_id === '5' || selectedDevice?.type_id === '23' || selectedDevice?.type_id === '10'),
        },
        {
            text: t('settings'),
            url: `/settings/device/device-settings/${selectedDevice ? selectedDevice.id : ''}`,
            className: 'blue',
            hidden: !(selectedDevice),
        },
        {
            text: t('edit'),
            url: `/settings/device/edit/${selectedDevice ? selectedDevice.id : ''}`,
            className: 'orange',
            hidden: !(selectedDevice),
        },
        {
            text: t('create_device'),
            url: '/settings/device/edit',
            className: 'green',
        },
    ], [t, selectedDevice, isBooster, onRFTest]);


    const displayNameExtractor = useCallback((item: BridgeDeviceModel, column: ColumnModel) => {
        if (column.key === 'type') {
            return bridgeDeviceTypeSet[item.type_id].name;
        }

        if (column.key === 'gateway_id') {
            return (gatewaySet[item.gateway_id] && gatewaySet[item.gateway_id].name) || '-';
        }

        return item[column.key] || '-';
    }, [bridgeDeviceTypeSet, gatewaySet]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: `${t('devices')} (${deviceCountValue})`,
            key: 'name',
        },
        {
            length: 2,
            name: t('type'),
            key: 'type',
        },
        {
            length: 2,
            name: t('unique_id'),
            key: 'unique_device_id',
        },
        {
            length: 1,
            name: t('rf'),
            key: 'rf_address',
        },
        {
            length: 2,
            name: t('gateway'),
            key: 'gateway_id',
        },
        {
            length: 2,
            name: t('rf_power'),
            key: "rf_signal_strength"
        }
    ], [t, deviceCountValue]);

    useEffect(() => {
        rpc.getDeviceCount().then(r => setDeviceCountValue(r))
    }, [rpc])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="device-list-page"
                backUrl={'/settings'}
                searchable={true}>
                <SettingsList
                    columns={columns}
                    items={bridgeDeviceList}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selectedDevice}
                    onItemSelected={setSelectedDevice}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
